import { AspectRatio } from '@/components/aspect-ratio';
import { CardContent } from '@/components/card';
import { ConferenceContext } from '@/store';
import { findCurrentDayId } from '@/lib/utils/DataFormat';
import { getSpacerValue } from '@/lib/utils/format';
import { useNodeProps } from '@/node-editor/hooks';
import { IDay } from '@/types/day';
import { INode, INodeProps } from '@/types/node';
import { IProgramRoom } from '@/types/room';
import { Grid, Icon, Typography, Theme, Box } from '@mui/material';
import React, { Fragment, useContext } from 'react';
import C from '@shared/constants/general';
import { getImage, ImageSize } from '@shared/utils/imageFormat';
import { IContent } from './contract';
import { useTheme } from '@mui/material/styles';

interface INodeExtended extends Omit<INode, 'content'> {
  content: IContent;
}

interface IProps extends Omit<INodeProps, 'node'> {
  node: INodeExtended;
}

interface IContainerStyleProps extends Pick<IContent, 'verticalSpacing'> {
  rooms: number;
  override: Record<string, any>;
}

interface IRoomStyleProps {
  backgroundImage: string;
  useDefaultImage: boolean;
}

const useContainerStyles = (
  theme: Theme,
  { verticalSpacing, rooms, override }: IContainerStyleProps
) => ({
  holder: {
    width: '100% !important',
    paddingTop: `${theme.spacing(
      getSpacerValue(verticalSpacing.top)
    )} !important`,
    paddingBottom: `${theme.spacing(
      getSpacerValue(verticalSpacing.bottom)
    )} !important`,
    ...override.root,
  },
  grid: {
    overflowX: 'auto !important',
    [`${theme.breakpoints.down('sm')}`]: {
      justifyContent: 'flex-start !important',
      flexWrap: 'nowrap !important',
    },
  },
  item: {
    borderRadius: '5px !important',
    flexGrow: '0 !important',
    flexShrink: '0 !important',
    [`${theme.breakpoints.down('sm')}`]: {
      maxWidth: rooms === 1 ? '100%' : '75% !important',
      flexBasis: rooms === 1 ? '100%' : '75% !important',
    },
  },
});

const useRoomStyles = (
  theme: Theme,
  { backgroundImage, useDefaultImage }: IRoomStyleProps
) => ({
  live: {
    width: '100% !important',
    display: 'flex !important',
    alignItems: 'center !important',
    marginBottom: 'auto !important',
    position: 'absolute !important',
    top: '0 !important',
    left: '0 !important',
    color: '#f44336 !important',
  },
  cardTypography: {
    width: '100% !important',
    display: 'flex !important',
    alignItems: 'center !important',
    justifyContent: 'center !important',
    backgroundColor: 'rgb(0,0,0,.4) !important',
    padding: `8px 0 !important`,
    borderRadius: '0 0 5px 5px !important',
    position: 'absolute !important',
    bottom: '0 !important',
    left: '0 !important',
    right: '0 !important',
    color: '#ffffff !important',
    '& > .material-icons': {
      marginRight: '4px !important',
    },
  },
  aspectContainer: {
    borderRadius: '5px !important',
    backgroundColor: `${theme.palette.primary.main} !important`,
    transition: 'box-shadow .3s linear !important',
    '&:hover, &:focus-within': {
      cursor: 'pointer !important',
      boxShadow: `0 0 0 2px ${theme.palette.primary.main} !important`,
    },
  },
  aspectContent: {
    '&:before': {
      content: '""',
      top: `0 !important`,
      right: `0 !important`,
      bottom: `0 !important`,
      left: `0 !important`,
      display: 'block !important',
      position: 'absolute !important',
      opacity: `${useDefaultImage ? 0.6 : 1} !important`,
      borderRadius: '5px !important',
      backgroundImage: `url(${backgroundImage}) !important`,
      backgroundSize: 'cover !important',
      backgroundRepeat: 'no-repeat !important',
      backgroundPosition: 'center center !important',
    },
  },
});

const Room = ({ room, index }: { room: IProgramRoom; index: number }) => {
  const theme = useTheme();
  const classes = useRoomStyles(theme, {
    backgroundImage: getImage(
      room.bg_picture || `/coeo/room_bg_${index % 5}.jpg`,
      ImageSize.Medium
    ),
    useDefaultImage: !room.bg_picture,
  });

  return (
    <AspectRatio
      maxHeight="150px"
      styles={{
        aspectContainer: classes.aspectContainer,
        aspectContent: classes.aspectContent,
      }}
    >
      <Box
        component="a"
        href={`/live/room/${room.id}`}
        sx={{
          width: '100% !important',
          height: '100% !important',
          zIndex: 1,
        }}
      >
        <CardContent contentJustification="flex-end" color="#fff">
          <Fragment>
            {Boolean(room.currentEventId) && (
              <Typography sx={classes.live} variant="subtitle1">
                <Icon>fiber_manual_record</Icon>
                LIVE
              </Typography>
            )}
            <Typography sx={classes.cardTypography} variant="body1">
              <Icon>videocam</Icon>
              {room.title}
            </Typography>
          </Fragment>
        </CardContent>
      </Box>
    </AspectRatio>
  );
};

export const Render = ({
  node: {
    id,
    override,
    content: { cardSize, verticalSpacing },
  },
}: IProps) => {
  const { conferenceState } = useContext(ConferenceContext); // Hämta Context
  if (conferenceState.conferenceDetail.programDays.length === 0) {
    return null;
  }
  const currentDayId = findCurrentDayId(conferenceState.conferenceDetail);
  const currentDay = conferenceState.conferenceDetail.programDays.find(
    (d: IDay) => d.id === currentDayId
  );
  if (!currentDay) {
    return null;
  }

  const programRooms = currentDay.programRooms.filter(
    ({ video_state }: any) => video_state !== C.PROGRAM_ROOM.VIDEO_STATE.FILE
  );
  const theme = useTheme();
  const classes = useContainerStyles(theme, {
    verticalSpacing,
    rooms: programRooms.length,
    override,
  });

  return (
    currentDay && (
      <Box sx={classes.holder} {...useNodeProps(id)}>
        <Grid container spacing={1} justifyContent="center" sx={classes.grid}>
          {programRooms.map((room: IProgramRoom, index: number) => (
            <Grid
              item
              key={room.id}
              sx={classes.item}
              size={{ md: parseInt(cardSize) as 12 | 6 | 4 | 3 }}
            >
              <Room room={room} index={index} />
            </Grid>
          ))}
        </Grid>
      </Box>
    )
  );
};
